import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Box,
  TextField,
  Typography,
  Grid,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Modal,
  Switch,
  Divider,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { AttachFile as AttachFileIcon, Clear as ClearIcon } from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditBranch = () => {
  const theme = useTheme();
  const { BranchId } = useParams();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Main branch confirmation modal
  const [showMainBranchConfirmModal, setShowMainBranchConfirmModal] = useState(false);
  const [mainBranchConfirmMessage, setMainBranchConfirmMessage] = useState('');

  // Dropdown data
  const [organizations, setOrganizations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const navigate = useNavigate();
  const MAX_FILE_SIZE = 1 * 1024 * 1024;

  const [formData, setFormData] = useState({
    organization: '',
    name: '',
    country: '',
    state: '',
    city: '',
    address: '',
    email: '',
    mobile: '',
    landline: '',
    zipcode: '',
    tin_number: '',
    affiliation_number: '',
    fax: '',
    website: '',
    bank_name: '',
    bank_branch: '',
    account_holder: '',
    account_number: '',
    ifsc_code: '',
    gst_number: '',
    image: null,
    imagePreview: null,
    existingImage: null,
    is_main_branch: false,
  });

  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await axios.get(`${API_BASE_URL}/api/master/organizations/`, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        setOrganizations(response.data);
      } catch (err) {
        console.error("Failed to fetch organizations:", err);
      }
    };
    fetchOrganizations();
  }, []);

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await axios.get(`${API_BASE_URL}/api/master/countries/`, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        setCountries(response.data);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (formData.country) {
        try {
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/states/?country=${formData.country}`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );
          const filteredStates = response.data.filter(
            (state) => state.country === parseInt(formData.country)
          );
          setStates(filteredStates);

          if (!isInitialLoad) {
            setFormData((prevData) => ({ ...prevData, state: '', city: '' }));
          }
        } catch (err) {
          console.error("Failed to fetch states:", err);
        }
      }
    };
    fetchStates();
  }, [formData.country]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (formData.state) {
        try {
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/cities/?state=${formData.state}`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );
          const filteredCities = response.data.filter(
            (city) => city.state === parseInt(formData.state)
          );
          setCities(filteredCities);

          if (!isInitialLoad) {
            setFormData((prevData) => ({ ...prevData, city: '' }));
          }
        } catch (err) {
          console.error("Failed to fetch cities:", err);
        }
      }
    };
    fetchCities();
  }, [formData.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch branch data
  useEffect(() => {
    const fetchBranch = async () => {
      if (BranchId) {
        try {
          setLoading(true);
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/branch/${BranchId}/`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );

          const branchData = response.data;

                   // The API returns a relative path (e.g. "/media/branch_images/xyz.png"),
          // so we prepend the base URL to make it a loadable image URL.
          const existingImageUrl = branchData.image
            ? `${API_BASE_URL}${branchData.image}`
            : null;

          const formattedData = {
            organization: branchData.organization || '',
            name: branchData.name || '',
            country: branchData.country || '',
            state: branchData.state || '',
            city: branchData.city || '',
            address: branchData.address || '',
            email: branchData.email || '',
            mobile: branchData.mobile || '',
            landline: branchData.landline || '',
            zipcode: branchData.zipcode || '',
            tin_number: branchData.tin_number || '',
            affiliation_number: branchData.affiliation_number || '',
            fax: branchData.fax || '',
            website: branchData.website || '',
            bank_name: branchData.bank_name || '',
            bank_branch: branchData.bank_branch || '',
            account_holder: branchData.account_holder || '',
            account_number: branchData.account_number || '',
            ifsc_code: branchData.ifsc_code || '',
            gst_number: branchData.gst_number || '',
            image: null,
            imagePreview: null,
            existingImage: existingImageUrl,
            is_main_branch: branchData.is_main_branch || false,
          };

          setFormData(formattedData);
          setTimeout(() => setIsInitialLoad(false), 500);

        } catch (err) {
          setSnackbarMessage('Failed to fetch branch details');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
          if (err.response?.status === 401) {
            localStorage.removeItem('accessToken');
            navigate('/login');
          }
        } finally {
          setLoading(false);
        }
      }
    };

    fetchBranch();
  }, [BranchId]);
const handleChange = (e) => {
  const { name, value, files, checked, type } = e.target;

  if (name === "image" && files && files[0]) {
    const file = files[0];

    if (file.size > MAX_FILE_SIZE) {
      setSnackbarMessage(
        "Image size exceeds 1MB limit. Please choose a smaller image."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      e.target.value = "";
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      image: file,
      imagePreview: URL.createObjectURL(file),
    }));

  } else if (type === "checkbox" || name === "is_main_branch") {

    setFormData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));

  } else {

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

  }
};

  const handleRemoveImage = () => {
    setFormData((prevData) => ({
      ...prevData,
      image: null,
      imagePreview: null,
    }));
  };

  const buildFormDataPayload = (extraFields = {}) => {
    const formDataToSend = new FormData();

    formDataToSend.append('organization', formData.organization);
    formDataToSend.append('name', formData.name);
    formDataToSend.append('country', formData.country);
    formDataToSend.append('state', formData.state);
    formDataToSend.append('city', formData.city);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('mobile', formData.mobile);
    formDataToSend.append('is_main_branch', formData.is_main_branch);

    if (formData.landline) formDataToSend.append('landline', formData.landline);
    if (formData.zipcode) formDataToSend.append('zipcode', formData.zipcode);
    if (formData.tin_number) formDataToSend.append('tin_number', formData.tin_number);
    if (formData.affiliation_number) formDataToSend.append('affiliation_number', formData.affiliation_number);
    if (formData.fax) formDataToSend.append('fax', formData.fax);
    if (formData.website) formDataToSend.append('website', formData.website);
    if (formData.bank_name) formDataToSend.append('bank_name', formData.bank_name);
    if (formData.bank_branch) formDataToSend.append('bank_branch', formData.bank_branch);
    if (formData.account_holder) formDataToSend.append('account_holder', formData.account_holder);
    if (formData.account_number) formDataToSend.append('account_number', formData.account_number);
    if (formData.ifsc_code) formDataToSend.append('ifsc_code', formData.ifsc_code);
    if (formData.gst_number) formDataToSend.append('gst_number', formData.gst_number);

    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }

    Object.entries(extraFields).forEach(([key, val]) => {
      formDataToSend.append(key, val);
    });

    return formDataToSend;
  };

  const submitUpdate = async (extraFields = {}) => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem('accessToken');
      const formDataToSend = buildFormDataPayload(extraFields);

      const response = await axios.put(
        `${API_BASE_URL}/api/master/branch/${BranchId}/`,
        formDataToSend,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const responseData = response.data;

      // In case API returns confirm_main_branch_change with HTTP 200
      if (responseData.confirm_main_branch_change) {
        const confirmMsg = Array.isArray(responseData.confirm_main_branch_change)
          ? responseData.confirm_main_branch_change[0]
          : responseData.confirm_main_branch_change;
        setMainBranchConfirmMessage(confirmMsg);
        setShowMainBranchConfirmModal(true);
        return;
      }

      setSnackbarMessage('Branch updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setTimeout(() => navigate(-1), 1000);

    } catch (err) {
      const errData = err.response?.data;

      // ✅ KEY FIX: API returns confirm_main_branch_change as HTTP 400
      // Axios throws it as error — we catch it here and show the modal instead of snackbar
      if (errData?.confirm_main_branch_change) {
        const confirmMsg = Array.isArray(errData.confirm_main_branch_change)
          ? errData.confirm_main_branch_change[0]
          : errData.confirm_main_branch_change;
        setMainBranchConfirmMessage(confirmMsg);
        setShowMainBranchConfirmModal(true);
        return; // Don't show snackbar, show modal instead
      }

      // All other real errors → show snackbar
      let errorMessage = errData?.message || 'Failed to update branch';
      if (errData && typeof errData === 'object') {
        const firstKey = Object.keys(errData)[0];
        if (firstKey && firstKey !== 'message') {
          const val = errData[firstKey];
          errorMessage = Array.isArray(val) ? val.join(', ') : String(val);
        }
      }
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);

      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.organization || !formData.name || !formData.country ||
      !formData.state || !formData.city || !formData.address ||
      !formData.email || !formData.mobile) {
      setSnackbarMessage('Please fill all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    await submitUpdate();
  };

  // User clicked "Yes, Confirm" in modal → resend with confirm_main_branch_change: true
  const handleConfirmMainBranchChange = async () => {
    setShowMainBranchConfirmModal(false);
    await submitUpdate({ confirm_main_branch_change: true });
  };

  // User clicked "Cancel" in modal → close modal and revert toggle
  const handleCancelMainBranchChange = () => {
    setShowMainBranchConfirmModal(false);
    setFormData((prevData) => ({
      ...prevData,
      is_main_branch: !prevData.is_main_branch,
    }));
  };

  const handleCancel = () => navigate(-1);

  const isFormValid = () => {
    return formData.organization && formData.name && formData.country &&
      formData.state && formData.city && formData.address &&
      formData.email && formData.mobile;
  };

  const textFieldStyle = {
    mb: 3,
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.text.primary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
  };

  const selectFieldStyle = {
    mb: 3,
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.text.primary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiSelect-select': {
      color: theme.palette.text.primary,
    },
  };

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Edit Branch Details
          </Typography>
        </Box>

        {loading && !formData.name ? (
          <Typography>Loading branch details...</Typography>
        ) : (
          <form onSubmit={handleSave}>
            <Grid container spacing={2}>
              {/* LEFT COLUMN */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={selectFieldStyle} disabled required>
                  <InputLabel>Organization *</InputLabel>
                  <Select
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    label="Organization *"
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Branch Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  sx={textFieldStyle}
                />

                <FormControl fullWidth sx={selectFieldStyle} required>
                  <InputLabel>Country *</InputLabel>
                  <Select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    label="Country *"
                    disabled
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={selectFieldStyle} required>
                  <InputLabel>State *</InputLabel>
                  <Select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    label="State *"
                    disabled
                  >
                    {states.map((state) => (
                      <MenuItem key={state.id} value={state.id}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={selectFieldStyle} required>
                  <InputLabel>City *</InputLabel>
                  <Select
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    label="City *"
                    disabled
                  >
                    {cities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Address *"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="Email *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="Mobile No. *"
                  name="mobile"
                  inputProps={{ maxLength: 10 }}
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="Landline No."
                  name="landline"
                  inputProps={{ maxLength: 10 }}
                  value={formData.landline}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="Zip Code"
                  name="zipcode"
                  value={formData.zipcode}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="TIN Number"
                  name="tin_number"
                  value={formData.tin_number}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Affiliation Number"
                  name="affiliation_number"
                  value={formData.affiliation_number}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="Fax"
                  name="fax"
                  value={formData.fax}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />
<TextField
  fullWidth
  label="Website"
  name="website"
  value={formData.website}
  onChange={handleChange}
  onBlur={() => {
    const website = formData.website.trim();

    if (
      website &&
      !website.startsWith("http://") &&
      !website.startsWith("https://")
    ) {
      setFormData((prevData) => ({
        ...prevData,
        website: `https://${website}`,
      }));
    }
  }}
  placeholder="example.com or https://example.com"
  helperText="URL will be auto-formatted with https://"
  sx={textFieldStyle}
/>

                <TextField
                  fullWidth
                  label="Bank Name"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="Bank Branch"
                  name="bank_branch"
                  value={formData.bank_branch}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="Account Holder Name"
                  name="account_holder"
                  value={formData.account_holder}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="Account Number"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="IFSC Code"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />

                <TextField
                  fullWidth
                  label="GST Number"
                  name="gst_number"
                  value={formData.gst_number}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />

                {/* Is Main Branch Toggle */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    border: `1px solid ${formData.is_main_branch ? theme.palette.primary.main : theme.palette.text.primary}`,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'border-color 0.2s ease',
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Set as Main Branch
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                      Designate this as the primary branch for the organization
                    </Typography>
                  </Box>
                  <Switch
                    name="is_main_branch"
                    checked={formData.is_main_branch}
                    onChange={handleChange}
                    color="primary"
                  />
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<AttachFileIcon />}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                  >
                    Choose Image
                    <input
                      type="file"
                      name="image"
                      onChange={handleChange}
                      accept="image/*"
                      hidden
                    />
                  </Button>
                </Box>

                <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 500, ml: 1, display: 'block', mb: 2 }}>
                  Maximum image size: 1MB
                </Typography>

                {(formData.imagePreview || formData.existingImage) && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                      src={formData.imagePreview || formData.existingImage}
                      alt="Preview"
                      style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                    />
                    {formData.imagePreview && (
                      <IconButton onClick={handleRemoveImage} color="error" size="small">
                        <ClearIcon />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!isFormValid() || loading}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
              >
                {loading ? 'UPDATING...' : 'UPDATE'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancel}
                disabled={loading}
              >
                CANCEL
              </Button>
            </Box>
          </form>
        )}
      </Box>

      {/* ✅ Main Branch Change Confirmation Modal */}
      <Modal
        open={showMainBranchConfirmModal}
        onClose={handleCancelMainBranchChange}
        aria-labelledby="main-branch-confirm-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 460,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography
              id="main-branch-confirm-title"
              variant="h6"
              sx={{ color: theme.palette.primary.contrastText, fontWeight: 'bold' }}
            >
              Main Branch Change ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '24px' }}>
            <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.primary }}>
              {mainBranchConfirmMessage}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: theme.palette.warning.main }}>
              This action will transfer the main branch status. Please confirm to proceed.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleCancelMainBranchChange}
                disabled={loading}
               data-app-cancel="true">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmMainBranchChange}
                disabled={loading}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
              >
                {loading ? 'Confirming...' : 'Yes, Confirm'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditBranch;